import { STORY_LANGUAGES } from "./story-languages";
/**
 * Single source of truth for what Chitravan offers, as shown on public pages
 * (home, pricing). Prices and credits mirror the credit_packs table and
 * api/credits_topup.py. Voice and language counts come from the API at render
 * time (see product-facts.server.ts); the numbers here are only fallbacks.
 */

export const SIGNUP_CREDITS = 125;

export type Pack = {
	id: "starter" | "creator" | "pro";
	name: string;
	priceInr: number;
	credits: number;
	blurb: string;
};

export const PACKS: Pack[] = [
	{ id: "starter", name: "Starter", priceInr: 499, credits: 500, blurb: "Everything you need to get started" },
	{ id: "creator", name: "Creator", priceInr: 1999, credits: 2100, blurb: "For creators who publish regularly" },
	{ id: "pro", name: "Pro", priceInr: 5999, credits: 6500, blurb: "For professional creators and agencies" },
];

/** Badges follow data, not taste: the best credits-per-rupee pack is "Best value". */
export function packBadge(pack: Pack): string | undefined {
	const best = [...PACKS].sort((a, b) => b.credits / b.priceInr - a.credits / a.priceInr)[0];
	if (pack.id === best.id) return "Best value";
	if (pack.id === "creator") return "Most popular";
	return undefined;
}

export const TOPUP = { minInr: 100, maxInr: 5000, stepInr: 100, fallbackCreditsPerRupee: 0.9 };

/** What each step costs today, in credits. */
export const COSTS = {
	script: 4,
	/** Credits for each second of a plain talking-free shot, and of a shot where the lips move (api/pricing.py: CREDITS_CLIP_PER_SEC_480P, CREDITS_CLIP_LIPSYNC_PER_SEC). */
	clipPerSec: 8,
	lipsyncPerSec: 8,
	/** The 3-second lip-sync test on a Cast card. */
	lipsyncTest: 24,   // 3 s of lip-sync (api/pricing.py credits_clip_lipsync(3)); keep in sync
	/** A voiceover costs this many credits for each character that is spoken (api/pricing.py CREDITS_PER_SPOKEN_CHAR); keep in sync. */
	voicePerChar: 0.0528,
	/** About how many characters a voice speaks in a second (api/pricing.py UNIT voiceover_chars_per_sec): turns a length into a quote. */
	voiceCharsPerSec: 16,
	voice30s: 26,      // ceil(30 s x 16 characters x 0.0528)
	keyframe: 2,
	/** One portrait design in the Cast step (api/pricing.py CREDITS_CHARACTER_IMAGE). */
	characterDesign: 2,
	/** How many picture options one request draws for a cast member: the person picks 1 to 10, 3 by default (api/routes/generate.py PICTURE_OPTIONS_MAX). */
	pictureOptionsDefault: 3,
	pictureOptionsMax: 10,
	/** Choosing and describing one cast member; one Idea Board round; planning the scenes of 30 seconds of film (api/pricing.py, Admin > Settings). */
	castMember: 3,
	ideaExplore: 10,
	scenePlan30s: 4,
	/** Hearing three versions of a designed voice; keeping one is free (api/pricing.py CREDITS_VOICE_DESIGN). */
	voiceDesign: 8,
	/** Credits for each second of film a scene's clip makes, and for removing the Chitravan logo from one export (api/pricing.py credits_clip_omni, api/routes/platform_v2.py). */
	clipOmniPerSec: 8,
	removeLogo: 25,
	/** Checking one upload (a picture, or up to 20 frames of a video) for sexual content and violence. */
	uploadCheck: 1,
	assembly: 2,
	video30s: 342,      // api/pricing.py typical_video_credits(30, 4); the app replaces it with the current number (loadPrices)
	video15s: 186,      // typical_video_credits(15, 3)
};

/** The owner sets these prices in Admin > Settings (api/pricing.py). Called once the app is open: the numbers below are replaced by the current ones, and a label drawn after this shows them.
 *  If the call fails the defaults above stay. */
export async function loadPrices(apiBase: string): Promise<void> {
	try {
		const r = await fetch(`${apiBase}/generate/prices`, { credentials: "include" });
		if (!r.ok) return;
		const p = (await r.json()) as Record<string, unknown>;
		const costs = COSTS as Record<string, number>;
		for (const [k, v] of Object.entries(p)) if (typeof v === "number" && k in costs) costs[k] = v;
	} catch { /* the defaults stay */ }
}

export type Costs = typeof COSTS;

export const costTable = (c: Costs) => [
	{ action: "AI script", credits: `${c.script}`, note: "per episode" },
	{ action: "Voiceover (30 seconds)", credits: `~${c.voice30s}`, note: "all characters" },
	{ action: "Scene image", credits: `${c.keyframe}`, note: "per scene" },
	{ action: "Cast member (made by the AI)", credits: `${c.castMember}`, note: "each character, place or prop" },
	{ action: "Picture option for a cast member", credits: `${c.characterDesign}`, note: "each; choose 1 to 10" },
	{ action: "Planning the scenes", credits: `${c.scenePlan30s}`, note: "per 30 seconds of film" },
	{ action: "Video scenes", credits: `${c.clipOmniPerSec}`, note: "per second of video" },
	{ action: "Final assembly", credits: `${c.assembly}`, note: "per video" },
	{ action: "A full 30-second video", credits: `~${c.video30s}`, note: "cast, script, voices, scenes and clips" },
];
export const COST_TABLE = costTable(COSTS);

/** The pricing page's cards: what each part of a video costs, in plain words. `icon` names a lucide icon. */
export const priceCards = (c: Costs) => [
	{ id: "story", icon: "ScrollText", title: "Story and script", price: `${c.script}`, unit: "per video", body: "Your idea is developed into a cast and a structured script with an opening hook and a clear ending." },
	{ id: "voices", icon: "Mic", title: "Voices", price: `~${c.voice30s}`, unit: "per 30 seconds", body: "Natural voices for every character, recorded in the language of your choice." },
	{ id: "cast", icon: "Users", title: "Cast", price: `${c.castMember}`, unit: "per character, place or prop the AI makes", body: "The AI chooses who is in your story and describes them. Adding someone yourself is free." },
	{ id: "pictures", icon: "Image", title: "Character and location art", price: `${c.characterDesign}`, unit: "per picture option", body: `Created with gpt-image. Start with ${c.pictureOptionsDefault} designs, ask for up to ${c.pictureOptionsMax}, and keep your favourite.` },
	{ id: "planning", icon: "ListChecks", title: "Scene planning", price: `${c.scenePlan30s}`, unit: "per 30 seconds of film", body: "The story is cut into scenes that follow the voices." },
	{ id: "clips", icon: "Clapperboard", title: "Video scenes", price: `${c.clipOmniPerSec}`, unit: "per second of video", body: "Every scene is generated with Google Omni Flash and synchronised with the voices." },
	{ id: "lipsync", icon: "Smile", title: "Lip-sync preview", price: `${c.lipsyncTest}`, unit: "per 3-second preview", body: "Preview a character speaking a line before you generate the full scene." },
	{ id: "export", icon: "Download", title: "Export", price: "Free", unit: "with the Chitravan logo", body: `Export without the logo for ${c.removeLogo} credits.` },
] as const;
export const PRICE_CARDS = priceCards(COSTS);

export const DURATION_RANGE = "15 seconds to 3 minutes";
export const BUILD_TIME = "about 30 minutes";

/**
 * Languages shown by name. Chosen from the languages that dominate YouTube
 * viewing and AI dubbing (Hindi, Spanish, Portuguese, Arabic, Indonesian,
 * French, German; sources in docs/platform/ux/home-content-audit.md). Every
 * one has voices in the library.
 */
export const FEATURED_LANGUAGES = (["hi", "en", "es", "pt", "ar", "id", "fr", "de"] as const).map((code) => ({
	code,
	label: STORY_LANGUAGES.find((l) => l.code === code)?.name ?? code,
}));

export const FALLBACK_VOICE_FACTS = { voices: 66, languageCount: 40 };

export function formatInr(rupees: number): string {
	return `₹${rupees.toLocaleString("en-IN")}`;
}

export function creditsPerRupee(pack: Pack): number {
	return pack.credits / pack.priceInr;
}

/** Questions shared by home and pricing. Numbers come from the constants above. */
export const faqItems = (c: Costs) => [
	{
		q: "Is it really free to start?",
		a: `Yes. You get ${SIGNUP_CREDITS} credits when you sign up, no card needed. That is enough to create one 30-second video or a few shorter ones.`,
	},
	{ q: "How long does a video take?", a: `${BUILD_TIME[0].toUpperCase() + BUILD_TIME.slice(1)}, including the time you take to review each step.` },
	{
		q: "What if I don't like a result?",
		a: "You can revise the idea, edit the script, or regenerate any individual scene. Regeneration uses credits, and the cost is shown before you confirm.",
	},
	{ q: "Do credits expire?", a: "No. Credits remain in your account until you use them." },
	{
		q: `What can I make with ${PACKS[0].credits} credits?`,
		a: `About ${(PACKS[0].credits / c.video30s).toFixed(1)} 30-second videos or ${(PACKS[0].credits / c.video15s).toFixed(1)} 15-second videos, from the cast to the finished scenes. A 30-second video uses about ${c.video30s} credits and a 15-second one about ${c.video15s}.`,
	},
	{ q: "Can I use the videos commercially?", a: "Yes. You own the rights to every video you create." },
	{
		q: "What can I upload, and what does it cost?",
		a: `You can upload pictures, videos and audio. A file can be up to 2 GB, a video up to 20 minutes, an episode can hold up to 6 GB of uploads, and an account up to 30 GB. Each picture or video is checked when it is uploaded, and the check costs ${c.uploadCheck} credit.`,
	},
	{
		q: "Is anything not allowed?",
		a: "Pictures and videos showing sexual content or violence are rejected, and the file is removed. The check cannot run on audio, so audio is only checked for size and type. If the check cannot run, the file is not accepted and nothing is charged.",
	},
	{ q: "How do I pay?", a: "We accept cards, and UPI in India, through Razorpay. Prices are displayed in rupees in India and in dollars elsewhere; all charges are made in rupees." },
];
export const FAQ_ITEMS = faqItems(COSTS);


/** 30 -> "30s", 120 -> "2 min", 90 -> "1 min 30s": how a length is said to the user. */
export function formatDuration(seconds: number): string {
	if (seconds < 60) return `${Math.round(seconds)}s`;
	const m = Math.floor(seconds / 60);
	const r = Math.round(seconds - m * 60);
	return r ? `${m} min ${r}s` : `${m} min`;
}

/** The price of a voiceover for a number of spoken characters, the same rule as api/pricing.py credits_voiceover_for_text. */
export function voiceCreditsForChars(chars: number): number {
	return Math.max(1, Math.ceil(Number((chars * COSTS.voicePerChar).toFixed(6))));
}

/** The price quoted for a length before the script exists, the same rule as api/pricing.py credits_voiceover. */
export function voiceCreditsForSeconds(seconds: number): number {
	return voiceCreditsForChars(seconds * COSTS.voiceCharsPerSec);
}
